import { retrieveDataFromLocalStorage } from "./localStorageUtils";

/**
 * Safely initialize state from localStorage with type validation
 * @param key - The localStorage key to retrieve
 * @param defaultValue - The default value to return if data is invalid or missing
 * @param validator - Optional type validator function
 * @returns The stored value if valid, otherwise the default value
 */
export function initializeFromStorage<T>(key: string, defaultValue: T, validator?: (value: any) => value is T): T {
  try {
    const stored = retrieveDataFromLocalStorage(key);
    
    if (stored === null) {
      return defaultValue;
    }
    
    if (validator) {
      return validator(stored) ? stored : defaultValue;
    }
    
    return stored !== undefined ? stored : defaultValue;
    
  } catch (error) {
    console.warn(`Failed to retrieve ${key} from localStorage:`, error);
  }
  return defaultValue;
}

/**
 * Initialize state from localStorage with legacy key fallback
 * Tries the new key first, then falls back to legacy key if new key doesn't exist or fails validation.
 * Automatically removes the legacy key after successful migration.
 * @param newKey - The current/preferred localStorage key
 * @param legacyKey - The old localStorage key to fall back to
 * @param defaultValue - The default value to return if both keys are invalid or missing
 * @param validator - Optional type validator function that both keys must pass
 * @returns The stored value from newKey or legacyKey if valid, otherwise the default value
 */
export function initializeFromLegacyStorage<T>(
  newKey: string, 
  legacyKey: string, 
  defaultValue: T, 
  validator?: (value: any) => value is T
): T {
  try {
    // Try new key first
    const newStored = retrieveDataFromLocalStorage(newKey);
    
    if (newStored !== null && newStored !== undefined) {
      const isValid = validator ? validator(newStored) : true;
      if (isValid) {
        // Clean up legacy key if it exists
        localStorage.removeItem(legacyKey);
        return newStored;
      }
    }
    
    // Fall back to legacy key
    const legacyStored = retrieveDataFromLocalStorage(legacyKey);
    
    if (legacyStored !== null && legacyStored !== undefined) {
      const isValid = validator ? validator(legacyStored) : true;
      if (isValid) {
        // Remove legacy key after successful migration
        localStorage.removeItem(legacyKey);
        return legacyStored;
      }
    }
    
  } catch (error) {
    console.warn(`Failed to retrieve ${newKey} or ${legacyKey} from localStorage:`, error);
  }
  
  return defaultValue;
}


// Type validators
export const isValidString = (value: any): value is string => typeof value === 'string';
export const isValidBoolean = (value: any): value is boolean => typeof value === 'boolean';
export const isValidArray = (value: any): value is any[] => Array.isArray(value);
export const isValidObject = (value: any): value is any => value !== null && typeof value === 'object' && !Array.isArray(value);
